import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { InferType } from 'yup';

import { useSendOtp } from '@sbv-clients/data-access-api';
import { FunctionComponent, removeNonDigits } from '@sbv-clients/util-misc';

import { schema } from '../schema';

type FormValues = InferType<typeof schema>;

interface VerifyPhoneNumberResultContextValue {
  methods: UseFormReturn<FormValues>;
  handleSubmit: SubmitHandler<FormValues>;
}

const VerifyPhoneNumberResultContext = createContext<
  VerifyPhoneNumberResultContextValue | undefined
>(undefined);

export function useVerifyPhoneNumberResult(): VerifyPhoneNumberResultContextValue {
  return useContext(VerifyPhoneNumberResultContext)!;
}

export const VerifyPhoneNumberProvider: FunctionComponent = ({ children }) => {
  const verifyPhone = useSendOtp();

  const navigate = useNavigate();

  const methods = useForm<FormValues>({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmit = useCallback(
    async ({ phoneNumber }: FormValues) => {
      try {
        const { data } = await verifyPhone.mutateAsync({
          phoneNumber: phoneNumber.replace(removeNonDigits, ''),
        });
        const { phoneNumber: phoneNumberResponse, isNewUser } = data;
        if (isNewUser) {
          navigate(`/auth/register?phoneNumber=${phoneNumberResponse}`);
        } else {
          navigate(`/auth/reset-password`);
        }
      } catch {
        /* empty */
      }
    },
    [navigate, verifyPhone]
  );

  const context: VerifyPhoneNumberResultContextValue = useMemo(
    () => ({
      methods,
      handleSubmit,
    }),
    [handleSubmit, methods]
  );

  return (
    <VerifyPhoneNumberResultContext.Provider value={context}>
      {children}
    </VerifyPhoneNumberResultContext.Provider>
  );
};
